import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, Loader2, Search, Trash2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const QUICK = ["Retinol", "Salicylic acid", "Hyaluronic acid", "Niacinamide", "Azelaic acid", "AHA/BHA", "Vitamin C", "Peptides", "Ceramides", "Bakuchiol"];

const VERDICT_CLASS = {
  great: "text-accent",
  okay: "text-foreground",
  caution: "text-amber-600",
  avoid: "text-destructive",
};

export default function BeautyIngredients() {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/beauty/ingredient/history");
      setHistory(data.lookups || []);
    } finally { setHistLoading(false); }
  };

  useEffect(() => { loadHistory(); }, []);

  const search = async (q) => {
    const text = (q ?? query).trim();
    if (!text) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/beauty/ingredient", { query: text });
      setResult(data.result);
      loadHistory();
    } catch (e) {
      toast.error("Lookup failed. Please retry.");
    } finally { setBusy(false); }
  };

  const removeHist = async (id) => {
    try {
      await api.delete(`/beauty/ingredient/history/${id}`);
      setHistory((prev) => prev.filter(h => h.id !== id));
    } catch { toast.error("Couldn't delete."); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-ingredients-page">
      <p className="eyebrow flex items-center gap-2"><FlaskConical size={12} /> Ingredient Intelligence</p>
      <h1 className="editorial-h1 mt-3">Know what&apos;s <em>in the bottle.</em></h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Paste any product&apos;s ingredient list — we&apos;ll flag what&apos;s working for you, what to skip, and what pairs well.
      </p>

      <div className="mt-8 grid lg:grid-cols-[1.4fr,1fr] gap-10">
        <section>
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") search(); }}
              placeholder="e.g. Niacinamide 10% or paste full ingredient list"
              className="rounded-none h-12 text-sm"
              data-testid="beauty-ingredients-input"
            />
            <Button
              onClick={() => search()}
              disabled={busy || !query.trim()}
              className="rounded-none h-12 uppercase tracking-[0.18em] text-xs px-6"
              data-testid="beauty-ingredients-search"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <><Search size={14} className="mr-2" /> Lookup</>}
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2" data-testid="beauty-ingredients-quick">
            {QUICK.map(q => (
              <button
                key={q}
                type="button"
                onClick={() => { setQuery(q); search(q); }}
                data-testid={`beauty-ingredient-quick-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-[10px] uppercase tracking-[0.16em] border border-border px-3 py-1.5 hover:border-foreground"
              >{q}</button>
            ))}
          </div>

          {busy && (
            <div className="mt-8 border border-border p-10 text-center">
              <Loader2 size={22} className="mx-auto animate-spin" />
              <p className="eyebrow mt-4">Reading the label</p>
            </div>
          )}

          {result && !busy && <IngredientResult data={result} />}

          {!result && !busy && (
            <div className="mt-10 border border-dashed border-border p-10 text-center">
              <Sparkles size={22} strokeWidth={1.3} className="mx-auto opacity-60" />
              <p className="font-serif text-xl mt-4">Try a quick ingredient above.</p>
              <p className="text-xs text-muted-foreground mt-2">Or paste your favourite serum&apos;s label and we&apos;ll dissect it.</p>
            </div>
          )}
        </section>

        {/* History sidebar */}
        <aside data-testid="beauty-ingredients-history">
          <p className="eyebrow">Recent lookups</p>
          {histLoading && <div className="mt-3 p-4 border border-dashed border-border text-center"><Loader2 size={16} className="animate-spin mx-auto" /></div>}
          {!histLoading && history.length === 0 && (
            <p className="text-xs text-muted-foreground mt-3">Nothing yet — your last 50 lookups will collect here.</p>
          )}
          <ul className="mt-4 space-y-2">
            {history.map((h) => {
              const v = h.result?.suitability?.verdict;
              const cls = VERDICT_CLASS[v] || "text-muted-foreground";
              return (
                <li key={h.id} className="border border-border p-3 group" data-testid={`beauty-ingredients-history-${h.id}`}>
                  <button
                    type="button"
                    onClick={() => { setQuery(h.query); setResult(h.result); }}
                    className="w-full text-left"
                  >
                    <p className="text-sm truncate">{h.result?.ingredient || h.query}</p>
                    {v && <p className={`text-[10px] uppercase tracking-[0.2em] mt-1 ${cls}`}>{v}</p>}
                  </button>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{new Date(h.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
                    <button
                      type="button"
                      onClick={() => removeHist(h.id)}
                      className="hover:text-destructive"
                      data-testid={`beauty-ingredients-history-delete-${h.id}`}
                      aria-label="Remove lookup"
                    ><Trash2 size={13} /></button>
                  </div>
                </li>
              );
            })}
          </ul>
        </aside>
      </div>
    </div>
  );
}

function IngredientResult({ data }) {
  const cls = VERDICT_CLASS[data?.suitability?.verdict] || "text-foreground";
  return (
    <div className="mt-8 border border-border p-6" data-testid="beauty-ingredients-result">
      <p className="font-serif text-3xl">{data.ingredient || "Ingredient"}</p>
      {data.purpose && <p className="text-sm text-muted-foreground mt-2">{data.purpose}</p>}
      {data.suitability?.verdict && (
        <div className="mt-4 flex items-baseline gap-3">
          <p className={`text-xs uppercase tracking-[0.22em] font-semibold ${cls}`}>{data.suitability.verdict}</p>
          {data.suitability.reason && <p className="text-sm text-muted-foreground">{data.suitability.reason}</p>}
        </div>
      )}
      {data.benefits?.length > 0 && (
        <div className="mt-6">
          <p className="eyebrow mb-2">Benefits</p>
          <ul className="text-sm space-y-1 list-disc list-inside">{data.benefits.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}
      {data.potential_irritants?.length > 0 && (
        <div className="mt-6">
          <p className="eyebrow text-destructive mb-2">Watch out</p>
          <ul className="text-sm space-y-1 list-disc list-inside text-destructive">{data.potential_irritants.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}
      {data.common_allergens?.length > 0 && (
        <div className="mt-4 text-xs text-muted-foreground">Common allergens · {data.common_allergens.join(", ")}</div>
      )}
      {data.stacking_advice?.length > 0 && (
        <div className="mt-6">
          <p className="eyebrow text-accent mb-2">Pairs well with</p>
          <ul className="text-sm space-y-1 list-disc list-inside text-accent">{data.stacking_advice.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
